import { Box, Typography } from '@mui/material';
import React, { FC, MouseEvent, ReactNode, useEffect, useState } from 'react';

import { formatToCurrency } from 'infrastructure/targets/web/modules/common/helpers';

import {
  InfoCardVariantStyledToggleButtonGroup,
  StyledToggleButtonGroup,
} from './components/ExtendedToggleButtonGroup/style';
import { StyledButtonsGroupContainer, StyledButtonSubtitle, StyledToggleButton } from './style';
import AlternativeLocationBadge from './components/AlternativeLocationBadge';
import {
  StyledButtonLabel,
  StyledButtonTitle,
  StyledToggleButton as InfoCardsStyledToggleButton,
  StyledButtonSubtitle as InfoCardsStyledButtonSubtitle,
} from './components/InfoCardsToggleButtonGroup/style';

export interface IButtonGroupButton {
  title: ReactNode;
  subtitle?: ReactNode;
  value: string | number;
  additionalValue?: string | number;
  isDisabled?: boolean;
  secondaryValue?: unknown;
  tertiaryValue?: unknown;
  availableForSmallEvents?: boolean;
  price?: number;
  highlight?: boolean;
  label?: string;
  alternativeLocations?: Array<{ locationId: string; locationName: string }>;
}

export interface IButtonsGroup {
  buttonList?: Array<IButtonGroupButton>;
  onChange?: (value: string, event: MouseEvent<HTMLElement>) => void;
  shouldStayInOneRow?: boolean;
  buttonSections?: Array<ButtonSection>;
  selectedValue?: string | number;
  nonClickable?: boolean;
  disabledItems?: number[];
  isSmallEvent?: boolean;
  hideDisabled?: boolean;
  infoCardVariant?: boolean;
  onLocationChange?: (locationId: string) => void;
}

export type ButtonSection = {
  sectionTitle: string;
  sectionValue: string;
  buttonList: Array<IButtonGroupButton>;
};

const ButtonsGroup: FC<IButtonsGroup> = ({
  buttonList,
  onChange,
  shouldStayInOneRow,
  buttonSections,
  selectedValue,
  nonClickable,
  disabledItems,
  isSmallEvent,
  hideDisabled,
  infoCardVariant,
  onLocationChange,
}) => {
  const [selectedButtonValue, setSelectedButtonValue] = useState<string | number>('');

  const handleChange = (newValue: string, event: MouseEvent<HTMLElement>) => {
    // Prevent selection of alternative-only buttons
    const target = event.currentTarget as HTMLElement;
    const isAlternativeOnly = target.dataset.alternativeonly === 'true';

    if (isAlternativeOnly) {
      event.preventDefault();
      event.stopPropagation();
      return;
    }

    if (onChange) {
      onChange(newValue, event);
    }
    setSelectedButtonValue(newValue);
  };

  useEffect(() => {
    if (selectedValue === 'unset') {
      setSelectedButtonValue('');
    }
    if (selectedValue) {
      setSelectedButtonValue(selectedValue);
    }
  }, [selectedValue]);

  return (
    <StyledButtonsGroupContainer>
      <>
        {buttonSections ? (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: '16px', width: '100%' }}>
            {buttonSections?.map((section, idx) => (
              <Box
                key={`${section}${idx}`}
                sx={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                <Typography variant={'h5'}>{section?.sectionTitle}</Typography>
                <StyledToggleButtonGroup
                  color="secondary"
                  value={selectedButtonValue}
                  numberofbuttons={section?.buttonList?.length}
                  shouldstayinonerow={shouldStayInOneRow ? 1 : 0}
                  exclusive
                  onChange={(event, val) => handleChange(val, event)}>
                  {section?.buttonList?.map(
                    (
                      {
                        title,
                        value,
                        isDisabled,
                        subtitle,
                        additionalValue,
                        secondaryValue,
                        tertiaryValue,
                        availableForSmallEvents,
                        price,
                        alternativeLocations,
                      },
                      idx,
                    ) => {
                      const hasAlternatives =
                        alternativeLocations && alternativeLocations.length > 0 && onLocationChange;
                      const wouldBeDisabled =
                        isDisabled || disabledItems?.includes(value as number);
                      const isAlternativeOnly = hasAlternatives && price === 0; // Only available at other locations

                      // For party size 7-12, completely hide 30/60 min slots even if alternatives exist
                      // These durations are not valid for 7-12 party bookings regardless of location
                      if (isSmallEvent && disabledItems?.includes(value as number)) {
                        return null;
                      }

                      // For party size 7-12, hide duration buttons where fewer than 2 simulators are available
                      // — charging the convenience fee without guaranteeing two sims side-by-side would be incorrect
                      if (isSmallEvent && availableForSmallEvents === false) {
                        return null;
                      }

                      // Only truly disable if no alternatives exist
                      const disabled = wouldBeDisabled && !hasAlternatives;

                      if (hideDisabled && wouldBeDisabled && !hasAlternatives) {
                        return null;
                      }

                      return (
                        <StyledToggleButton
                          data-testid={`buttonGroupButton-${value}`}
                          key={`${idx}${value}${section?.sectionValue}`}
                          data-sectionvalue={`${section?.sectionValue}`}
                          data-additionalvalue={`${additionalValue}`}
                          data-secondaryvalue={`${secondaryValue}`}
                          data-tertiaryvalue={`${tertiaryValue}`}
                          data-alternativeonly={isAlternativeOnly ? 'true' : 'false'}
                          value={value + '/' + section?.sectionValue}
                          sx={{
                            flexDirection: 'column',
                            gap: '4px',
                            // Make alternative-only buttons look disabled (light grey)
                            ...(isAlternativeOnly && {
                              // backgroundColor: '#fafafa !important',
                              borderColor: '#e0e0e0 !important',
                              cursor: 'default',
                              color: 'rgba(0, 0, 0, 0.38)',
                              '&:hover': {
                                // backgroundColor: '#fafafa !important',
                                borderColor: '#e0e0e0 !important',
                              },
                            }),
                            // Disabled buttons should have the same light grey appearance
                            ...(disabled && {
                              // backgroundColor: '#fafafa !important',
                              borderColor: '#e0e0e0 !important',
                              color: 'rgba(0, 0, 0, 0.38)',
                            }),
                          }}
                          disabled={disabled}>
                          <Typography variant="body2" sx={{ color: 'inherit' }}>
                            {title}
                          </Typography>
                          {subtitle && (
                            <StyledButtonSubtitle variant="body2">{subtitle}</StyledButtonSubtitle>
                          )}
                          {/* Show badge instead of price when alternatives exist */}
                          {hasAlternatives ? (
                            <AlternativeLocationBadge
                              locations={alternativeLocations}
                              onLocationChange={onLocationChange}
                            />
                          ) : (
                            !disabled &&
                            price > -1 && (
                              <StyledButtonSubtitle variant="body2">
                                {isSmallEvent
                                  ? `${formatToCurrency(2 * price)}`
                                  : `${formatToCurrency(price)}`}
                              </StyledButtonSubtitle>
                            )
                          )}
                        </StyledToggleButton>
                      );
                    },
                  )}
                </StyledToggleButtonGroup>
              </Box>
            ))}
          </Box>
        ) : infoCardVariant ? (
          <InfoCardVariantStyledToggleButtonGroup
            color="secondary"
            value={selectedButtonValue}
            numberofbuttons={buttonList?.length || 0}
            shouldstayinonerow={shouldStayInOneRow ? 1 : 0}
            exclusive
            onChange={(event, val) => (nonClickable ? null : handleChange(val, event))}>
            {buttonList?.map(
              (
                {
                  title,
                  value,
                  isDisabled,
                  subtitle,
                  additionalValue,
                  secondaryValue,
                  tertiaryValue,
                  price,
                  highlight,
                  label,
                },
                idx,
              ) => (
                <InfoCardsStyledToggleButton
                  data-testid={`buttonGroupButton-${value}`}
                  key={`${idx}${value}`}
                  value={value}
                  data-additionalvalue={`${additionalValue}`}
                  data-secondaryvalue={`${secondaryValue}`}
                  data-tertiaryvalue={`${tertiaryValue}`}
                  disableRipple={nonClickable}
                  disableFocusRipple={nonClickable}
                  sx={{
                    '&:hover': {
                      backgroundColor: nonClickable ? 'transparent' : '',
                    },
                    cursor: nonClickable ? 'default' : 'pointer',
                  }}
                  disabled={isDisabled || disabledItems?.includes(value as number)}>
                  <Box
                    component="span"
                    sx={{ display: 'inline-flex', height: '52px', alignItems: 'center' }}>
                    <StyledButtonTitle
                      sx={highlight && { fontWeight: '400', fontFamily: 'Womby', fontSize: 14 }}>
                      {title}
                    </StyledButtonTitle>
                  </Box>
                  {label && <StyledButtonLabel>{label}</StyledButtonLabel>}
                  {subtitle && (
                    <InfoCardsStyledButtonSubtitle
                      sx={
                        highlight && {
                          color: '#F50057',
                          fontFamily: 'Womby',
                          fontWeight: '400',
                          fontSize: 18,
                        }
                      }>
                      {subtitle}
                    </InfoCardsStyledButtonSubtitle>
                  )}
                  {!isDisabled && price > -1 && (
                    <StyledButtonSubtitle variant="body2">
                      {isSmallEvent
                        ? `${formatToCurrency(2 * price)}`
                        : `${formatToCurrency(price)}`}
                    </StyledButtonSubtitle>
                  )}
                </InfoCardsStyledToggleButton>
              ),
            )}
          </InfoCardVariantStyledToggleButtonGroup>
        ) : (
          <StyledToggleButtonGroup
            color="secondary"
            value={selectedButtonValue}
            numberofbuttons={buttonList?.length || 0}
            shouldstayinonerow={shouldStayInOneRow ? 1 : 0}
            exclusive
            onChange={(event, val) => (nonClickable ? null : handleChange(val, event))}>
            {buttonList?.map(
              (
                {
                  title,
                  value,
                  isDisabled,
                  subtitle,
                  additionalValue,
                  secondaryValue,
                  tertiaryValue,
                  availableForSmallEvents,
                  price,
                  alternativeLocations,
                },
                idx,
              ) => {
                const hasAlternatives =
                  alternativeLocations && alternativeLocations.length > 0 && onLocationChange;
                const wouldBeDisabled = isDisabled || disabledItems?.includes(value as number);
                const isAlternativeOnly = hasAlternatives && price === 0; // Only available at other locations

                // For party size 7-12, completely hide 30/60 min slots even if alternatives exist
                // These durations are not valid for 7-12 party bookings regardless of location
                if (isSmallEvent && disabledItems?.includes(value as number)) {
                  return null;
                }

                // For party size 7-12, hide duration buttons where fewer than 2 simulators are available
                // — charging the convenience fee without guaranteeing two sims side-by-side would be incorrect
                if (isSmallEvent && availableForSmallEvents === false) {
                  return null;
                }

                // Only truly disable if no alternatives exist
                const disabled = wouldBeDisabled && !hasAlternatives;

                return (
                  <StyledToggleButton
                    data-testid={`buttonGroupButton-${value}`}
                    key={`${idx}${value}`}
                    value={value}
                    data-additionalvalue={`${additionalValue}`}
                    data-secondaryvalue={`${secondaryValue}`}
                    data-tertiaryvalue={`${tertiaryValue}`}
                    data-alternativeonly={isAlternativeOnly ? 'true' : 'false'}
                    disableRipple={nonClickable}
                    disableFocusRipple={nonClickable}
                    sx={{
                      // minWidth: shouldStayInOneRow ? '100px' : '115px',
                      height: '64px',
                      '&:hover': {
                        backgroundColor: nonClickable ? 'transparent' : '',
                      },
                      cursor: nonClickable ? 'default' : 'pointer',
                      // Make alternative-only buttons look disabled (light grey)
                      ...(isAlternativeOnly && {
                        // backgroundColor: '#fafafa !important',
                        borderColor: '#e0e0e0 !important',
                        cursor: 'default',
                        color: 'rgba(0, 0, 0, 0.38)',
                        '&:hover': {
                          // backgroundColor: '#fafafa !important',
                          borderColor: '#e0e0e0 !important',
                        },
                      }),
                      // Disabled buttons should have the same light grey appearance
                      ...(disabled && {
                        // backgroundColor: '#fafafa !important',
                        borderColor: '#e0e0e0 !important',
                        color: 'rgba(0, 0, 0, 0.38)',
                      }),
                    }}
                    disabled={disabled}>
                    {title}
                    {subtitle && (
                      <StyledButtonSubtitle variant="body2">{subtitle}</StyledButtonSubtitle>
                    )}
                    {/* Show badge instead of price when alternatives exist */}
                    {hasAlternatives ? (
                      <AlternativeLocationBadge
                        locations={alternativeLocations}
                        onLocationChange={onLocationChange}
                      />
                    ) : (
                      !disabled &&
                      price > -1 && (
                        <StyledButtonSubtitle variant="body2">
                          {isSmallEvent
                            ? `${formatToCurrency(2 * price)}`
                            : `${formatToCurrency(price)}`}
                        </StyledButtonSubtitle>
                      )
                    )}
                  </StyledToggleButton>
                );
              },
            )}
          </StyledToggleButtonGroup>
        )}
      </>
    </StyledButtonsGroupContainer>
  );
};

export default ButtonsGroup;
